import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Info, Thermometer, Shield, X, ChevronRight } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

interface AdviceModal {
  id: string;
  title: string;
  content: React.ReactNode;
  icon: React.ElementType;
}

const TireAdviceSection: React.FC = () => {
  const { t, translations } = useLanguage();
  const [activeModal, setActiveModal] = useState<string | null>(null);

  const adviceItems = [
    {
      id: 'reading-tires',
      title: t('tires.advice.items.reading-tires.title'),
      icon: Info,
      description: t('tires.advice.items.reading-tires.description')
    },
    {
      id: 'winter-tires',
      title: t('tires.advice.items.winter-tires.title'),
      icon: Thermometer,
      description: t('tires.advice.items.winter-tires.description')
    },
    {
      id: 'storage',
      title: t('tires.advice.items.storage.title'),
      icon: Shield,
      description: t('tires.advice.items.storage.description')
    }
  ];

  const renderModalContent = (itemId: string) => {
    const contentData = translations.tires?.advice?.items?.[itemId]?.content;
    if (!contentData) return null;

    switch (itemId) {
      case 'reading-tires':
        return (
          <div className="space-y-6">
            <img 
              src="/pneu.jpg" 
              alt="Schéma des inscriptions sur un pneu" 
              className="w-full rounded-lg"
            />
            <div className="space-y-4">
              <p className="text-gray-600">
                {contentData.intro}
              </p>
              <div className="bg-red-50 p-4 rounded-lg">
                <h4 className="font-bold text-red-800 mb-2">{contentData.example?.title}</h4>
                <ul className="space-y-2 text-sm">
                  {contentData.example?.items?.map((item, index) => (
                    <li key={index}><strong>{item.split(' = ')[0]}</strong> = {item.split(' = ')[1]}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        );

      case 'winter-tires':
        return (
          <div className="space-y-6">
            <img 
              src="/pneu-neige.jpg" 
              alt="Pneus hiver" 
              className="w-full rounded-lg"
            />
            <div className="space-y-4">
              <p className="text-gray-600">{contentData.intro}</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {contentData.advantages?.map((advantage, index) => (
                  <div key={index} className={`bg-${advantage.color}-50 p-4 rounded-lg`}>
                    <h4 className={`font-bold text-${advantage.color}-800 mb-2`}>{advantage.title}</h4>
                    <p className={`text-sm text-${advantage.color}-700`}>{advantage.description}</p>
                  </div>
                ))}
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border-l-4 border-orange-400">
                <h4 className="font-bold text-orange-800 mb-2">{contentData.safety?.title}</h4>
                <p className="text-orange-700">{contentData.safety?.text}</p>
              </div>
            </div>
          </div>
        );

      case 'storage':
        return (
          <div className="space-y-6">
            <img 
              src="/gardiennage-pneu.jpg" 
              alt="Stockage des pneus" 
              className="w-full rounded-lg"
            />
            <div className="space-y-4">
              <p className="text-gray-600">{contentData.intro}</p>
              <h4 className="font-bold text-gray-800">Étapes de stockage :</h4>
              <ol className="space-y-3">
                {contentData.steps?.map((step, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <span className="bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
                      {step.number}
                    </span>
                    <div>
                      <strong>{step.title} :</strong> {step.description}
                    </div>
                  </li>
                ))}
              </ol>
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-bold text-blue-800 mb-2">{contentData.service?.title}</h4>
                <p className="text-blue-700">{contentData.service?.text}</p>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const openModal = (modalId: string) => {
    setActiveModal(modalId);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setActiveModal(null);
    document.body.style.overflow = 'unset';
  };

  return (
    <>
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {t('tires.advice.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('tires.advice.subtitle')}
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-red-600 to-red-500 mx-auto mt-8 rounded-full" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {adviceItems.map((item, index) => {
              const IconComponent = item.icon;
              
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  className="bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-8 border border-gray-100 group cursor-pointer"
                  onClick={() => openModal(item.id)}
                >
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    className="w-16 h-16 bg-gradient-to-r from-red-600 to-red-700 rounded-2xl flex items-center justify-center mb-6 mx-auto group-hover:shadow-lg transition-shadow duration-300"
                  >
                    <IconComponent className="w-8 h-8 text-white" />
                  </motion.div>

                  <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-red-600 transition-colors duration-300">
                    {item.title}
                  </h3>

                  <p className="text-gray-600 mb-6">
                    {item.description}
                  </p>

                  <motion.div
                    whileHover={{ x: 5 }}
                    className="flex items-center text-red-600 font-medium"
                  >
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </motion.div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Modal */}
      <AnimatePresence>
        {activeModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={closeModal}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 50 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-white rounded-2xl shadow-2xl max-w-4xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {adviceItems.find(item => item.id === activeModal) && (
                <>
                  <div className="bg-gradient-to-r from-red-600 to-red-700 p-6 flex items-center justify-between">
                    <h3 className="text-2xl font-bold text-white">
                      {adviceItems.find(item => item.id === activeModal)?.title}
                    </h3>
                    <motion.button
                      onClick={closeModal}
                      whileHover={{ scale: 1.1, rotate: 90 }}
                      whileTap={{ scale: 0.9 }}
                      className="p-2 bg-white/20 rounded-lg text-white hover:bg-white/30 transition-colors"
                    >
                      <X className="w-6 h-6" />
                    </motion.button>
                  </div>
                  <div className="p-8">
                    {renderModalContent(activeModal)}
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default TireAdviceSection;